/**
 * standaloneViewportFix — 修正 iOS PWA standalone 轉向後的視窗位移
 *
 * ── 問題 ──────────────────────────────────────────────────────
 * iPhone 以 standalone(加到主畫面)開啟、轉成橫向時,layout viewport 會被
 * safe-area 內縮,而文件同時取得一個不該存在的負捲動位移。實測(iOS 18.7):
 *
 *     innerW × innerH : 734 × 393
 *     outerW × outerH : 852 × 393      ← 差 118 = 59 × 2(左右各一個 inset)
 *     vv 位移 (L, T)  : 0, -59
 *     頁面捲動 (X, Y) : 0, -59
 *     vv scale        : 1.000          ← 不是縮放問題,是純垂直位移
 *
 * position:fixed 依 layout viewport 排版,觸控座標卻走 visual viewport,
 * 兩者就差了這 59px —— 畫出來的位置與實際命中區分家,使用者會覺得
 * 「觸發點在手指上方」。因為這是 iOS 的行為而不是我們的 CSS,所以
 * 完全不同的兩套前台會出現一模一樣的症狀,而一般瀏覽器分頁不會。
 *
 * ── 為什麼不用 viewport-fit=cover ──────────────────────────────
 * 那是教科書解法,但會讓版面延伸到 safe-area 底下,得在全站補
 * env(safe-area-inset-*),否則功能會被瀏海/home indicator 遮住。
 * 侵入性太高,所以改走「把虛假位移捲回去」這條路。
 *
 * ── 這裡的作法 ────────────────────────────────────────────────
 * · 只在 standalone 生效。一般瀏覽器分頁沒這個問題,而且有些頁面刻意
 *   讓頁面比視窗高來收合網址列,動了會壞掉。
 * · 用 visualViewport.offsetTop 當位移量,不是直接 scrollTo(0, 0)。
 *   這樣在「真的有捲動」的長列表上,只會扣掉虛假的那一段,
 *   使用者原本看到哪就還在哪,不會被踢回頂端。
 * · 縮放中(scale ≠ 1)或鍵盤叫起來時,offsetTop 本來就該是非 0,跳過。
 * · iOS 轉向後尺寸會晚幾拍才安定,所以補幾次重試。
 */

/** 這幾拍之後再各量一次,涵蓋 iOS 轉向動畫結束前後 */
const RETRY_DELAYS = [0, 150, 400, 800];

/* ── 自我回報 ──────────────────────────────────────────────────
   上一輪吃過一次虧:修正被掛在 MainLayout 上,而遊戲頁是獨立路由、
   不經過 MainLayout,所以那段程式從來沒執行過,但從外面完全看不出來。
   這裡把狀態掛到 window,線上可以直接確認「到底有沒有在跑」,
   分得出「沒部署 / 沒執行」和「有執行但沒效」。

   在 PWA 裡開不了 devtools,所以診斷頁會讀這個物件顯示出來。 */
const report = {
  installed: false,
  standalone: false,
  corrections: 0,   // 實際補償了幾次
  lastDrift: 0,     // 最近一次看到的位移
  lastFixedAt: null,
};
if (typeof window !== "undefined") window.__viewportFix__ = report;

export const isStandaloneDisplay = () =>
  (typeof window !== "undefined" &&
    (window.matchMedia?.("(display-mode: standalone)").matches ||
      window.navigator.standalone === true)) ||
  false;

/** 焦點在輸入元件上 → 鍵盤可能已推起 visual viewport,此時的位移是正常的 */
const isTypingTarget = () => {
  const el = document.activeElement;
  if (!el) return false;
  if (el.isContentEditable) return true;
  return /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName);
};

/**
 * 量一次並把虛假位移捲掉。
 * @returns {number} 實際補償掉的像素(0 代表沒動)
 */
let correcting = false;

export const correctViewportDrift = () => {
  if (typeof window === "undefined") return 0;
  const vv = window.visualViewport;
  if (!vv) return 0;

  /* 捏合縮放中,offsetTop 非 0 是正常的,不要干預 */
  if (vv.scale !== 1) return 0;
  if (isTypingTarget()) return 0;

  const drift = Math.round(vv.offsetTop);
  report.lastDrift = drift;
  if (drift === 0) return 0;

  /* 我們自己的 scrollBy 會再觸發一次 visualViewport 的 scroll 事件,
     沒有這道閘門就會自己叫自己。補完 drift 變 0 就自然停住。 */
  if (correcting) return 0;
  correcting = true;
  try {
    /* offsetTop = -59 代表 visual viewport 在 layout viewport 上方 59px,
       往回捲 +59 抵銷。用 scrollBy 而不是 scrollTo(0, 0),
       真正有捲動的頁面才不會被踢回頂端。 */
    window.scrollBy(0, -drift);
  } finally {
    correcting = false;
  }

  report.corrections += 1;
  report.lastFixedAt = Date.now();
  return -drift;
};

/**
 * 掛上監聽。只在 standalone 生效。
 * @returns {() => void} 解除監聽
 */
export const installStandaloneViewportFix = () => {
  if (typeof window === "undefined") return () => {};

  report.standalone = isStandaloneDisplay();
  if (!report.standalone) return () => {};

  let timers = [];

  const runWithRetries = () => {
    timers.forEach(clearTimeout);
    timers = RETRY_DELAYS.map((d) => setTimeout(correctViewportDrift, d));
  };

  window.addEventListener("orientationchange", runWithRetries);
  const so = window.screen?.orientation;
  so?.addEventListener?.("change", runWithRetries);
  /* 從背景切回來 / bfcache 還原時也可能帶著位移 */
  window.addEventListener("pageshow", runWithRetries);

  /* visualViewport 的 resize / scroll 是「位移改變的當下」就會觸發,
     比只等 orientationchange 準得多 —— 位移不見得只在轉向時出現
     (進遊戲頁、切換全螢幕、從背景返回都可能)。
     correcting 閘門確保我們自己的 scrollBy 不會反覆自我觸發。 */
  const vv = window.visualViewport;
  vv?.addEventListener?.("resize", correctViewportDrift);
  vv?.addEventListener?.("scroll", correctViewportDrift);

  /* 進站當下先校一次(使用者可能就是在橫向開啟的) */
  runWithRetries();
  report.installed = true;

  return () => {
    timers.forEach(clearTimeout);
    window.removeEventListener("orientationchange", runWithRetries);
    so?.removeEventListener?.("change", runWithRetries);
    window.removeEventListener("pageshow", runWithRetries);
    vv?.removeEventListener?.("resize", correctViewportDrift);
    vv?.removeEventListener?.("scroll", correctViewportDrift);
    report.installed = false;
  };
};
